import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountBalanceWallet,
  TrendingUp,
  Savings,
  Calculate,
  ShowChart,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import PageTitle from '../../components/PageTitle/PageTitle';

interface ToolCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
}

const ToolCard: React.FC<ToolCardProps> = ({ title, description, icon, path }) => {
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        backgroundColor: androidColors.cardBackground,
        border: `1px solid ${androidColors.borderColor}`,
        borderRadius: '12px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 16px rgba(0, 0, 0, 0.2)`,
          borderColor: androidColors.primaryGold,
        },
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: `${androidColors.primaryGold}20`,
              borderRadius: '12px',
              p: 1,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: androidColors.textColor,
              fontWeight: 'bold',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: androidColors.textSecondary,
            mb: 3,
            flex: 1,
          }}
        >
          {description}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate(path)}
          sx={{
            borderColor: androidColors.primaryGold,
            color: androidColors.primaryGold,
            '&:hover': {
              borderColor: androidColors.primaryGold,
              backgroundColor: `${androidColors.primaryGold}10`,
            },
            alignSelf: 'flex-start',
          }}
        >
          {/** This is used within card localized strings */}
          {useTranslation().t('tools.accessTool')}
        </Button>
      </CardContent>
    </Card>
  );
};

const Tools: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tools = [
    {
      title: t('tools.wealthTitle'),
      description: t('tools.wealthDesc'),
      icon: <AccountBalanceWallet sx={{ color: androidColors.primaryGold, fontSize: 28 }} />,
      path: '/tools/wealth-simulator',
    },
    {
      title: t('tools.compoundTitle'),
      description: t('tools.compoundDesc'),
      icon: <TrendingUp sx={{ color: androidColors.primaryGold, fontSize: 28 }} />,
      path: '/tools/compound-interest',
    },
    {
      title: t('tools.budgetTitle'),
      description: t('tools.budgetDesc'),
      icon: <Savings sx={{ color: androidColors.primaryGold, fontSize: 28 }} />,
      path: '/tools/crypto-budget',
    },
    {
      title: t('tools.simpleTitle'),
      description: t('tools.simpleDesc'),
      icon: <Calculate sx={{ color: androidColors.primaryGold, fontSize: 28 }} />,
      path: '/tools/simple-interest',
    },
    {
      title: t('tools.marketTitle'),
      description: t('tools.marketDesc'),
      icon: <ShowChart sx={{ color: androidColors.primaryGold, fontSize: 28 }} />,
      path: '/tools/market',
    },
  ];

  return (
    <Layout title={t('tools.title')}>
      <PageTitle title={t('tools.title')} />
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
            mb: 3,
          }}
        >
          {t('tools.financialTools')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: androidColors.textSecondary,
            mb: 4,
          }}
        >
          {t('tools.subtitle')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            '& > *': {
              flex: '1 1 calc(50% - 12px)',
              minWidth: { xs: '100%', sm: 'calc(50% - 12px)' },
            },
          }}
        >
          {tools.map((tool) => (
            <Box key={tool.path}>
              <ToolCard {...tool} />
            </Box>
          ))}
        </Box>
      </Box>
    </Layout>
  );
};

export default Tools; 